/**
 * rsi-mtf-page.js
 *
 * Scans all coins/stocks for the RSI Multi-Timeframe strategy:
 *
 *   LONG  — Weekly RSI > 50  |  Daily RSI > 45  |  4H (or 1H) RSI > 50  |  Price above rising 50 SMA
 *   SHORT — Weekly RSI < 50  |  Daily RSI <= 55  |  4H (or 1H) RSI < 50  |  Price below falling 50 SMA
 *
 * The page displays all coins/stocks meeting the base criteria.
 * Alerts fire only when the 4H/1H RSI crosses 50 (the trigger event).
 *
 * Uses VPS pre-computed indicators when available (fast-path),
 * falls back to live Binance kline fetching otherwise (crypto only).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import { fetchTopCoins } from '../services/coingecko-api';
import { fetchBinanceKlinesBatch } from '../services/binance-api';
import { buildSymbolIndex, isStablecoin } from '../utils/symbol-mapper';
import { parseBinanceKlines, extractPrices } from '../utils/timeframe-utils';
import { calculateRSI } from '../utils/technical-indicators';
import { formatPrice, formatPercentChange, formatRSI } from '../utils/format-helpers';
import { fetchServerMarketData, fetchStocksMarketData, isServerApiAvailable } from '../services/server-api';
import './rsi-mtf-page.css';

// ─── Strategy logic ───────────────────────────────────────────────────────────

/**
 * Evaluate whether a coin qualifies for a long or short RSI-MTF setup.
 * Returns 'long' | 'short' | null.
 */
function evaluateSignal(weeklyRsi, dailyRsi, fourHourRsi) {
  if (weeklyRsi == null || dailyRsi == null || fourHourRsi == null) return null;

  if (weeklyRsi > 50 && dailyRsi > 45 && fourHourRsi > 50) return 'long';
  if (weeklyRsi < 50 && dailyRsi <= 55 && fourHourRsi < 50) return 'short';

  return null;
}

/**
 * Check if the 4H RSI has JUST crossed 50 — used for alert triggering only.
 */
function hasCrossed50(prevRsi, currentRsi, direction) {
  if (prevRsi == null || currentRsi == null) return false;
  if (direction === 'long')  return prevRsi < 50 && currentRsi > 50;
  if (direction === 'short') return prevRsi > 50 && currentRsi < 50;
  return false;
}

// ─── RSI-MTF card ─────────────────────────────────────────────────────────────

function SignalCard({ coin, assetMode }) {
  const navigate = useNavigate();
  const isLong = coin.direction === 'long';
  const fourHourLabel = assetMode === 'stocks' ? '1H RSI' : '4H RSI';

  return (
    <div
      className={`mtf-card ${coin.direction}`}
      onClick={() => navigate(`/coin/${coin.id}`)}
      title="Click for details"
    >
      <div className="mtf-card-top">
        <div className="mtf-coin-identity">
          {coin.image && (
            <img src={coin.image} alt={coin.name} className="mtf-coin-logo"
              onError={e => e.target.style.display = 'none'} />
          )}
          <div>
            <div className="mtf-coin-symbol">{coin.symbol?.toUpperCase()}</div>
            <div className="mtf-coin-name">{coin.name}</div>
          </div>
        </div>
        <div className={`mtf-direction-badge ${coin.direction}`}>
          {isLong ? '▲ LONG' : '▼ SHORT'}
        </div>
      </div>

      <div className="mtf-indicators">
        <div className="mtf-ind-row">
          <span className="mtf-ind-label">Weekly RSI</span>
          <span className={`mtf-ind-value ${coin.weeklyRsi > 50 ? 'bull' : 'bear'}`}>
            {formatRSI(coin.weeklyRsi)}
          </span>
        </div>
        <div className="mtf-ind-row">
          <span className="mtf-ind-label">Daily RSI</span>
          <span className="mtf-ind-value zone">{formatRSI(coin.dailyRsi)}</span>
        </div>
        <div className="mtf-ind-row">
          <span className="mtf-ind-label">{fourHourLabel}</span>
          <span className={`mtf-ind-value ${isLong ? 'bull' : 'bear'}`}>
            {formatRSI(coin.fourHourRsi)}
            <span className="mtf-cross-label">
              {isLong ? ' ↑ above 50' : ' ↓ below 50'}
            </span>
          </span>
        </div>
      </div>

      <div className="mtf-card-footer">
        <div className="mtf-price-block">
          <span className="mtf-price">{formatPrice(coin.current_price)}</span>
          <span className={`mtf-change ${coin.price_change_percentage_24h >= 0 ? 'positive' : 'negative'}`}>
            {formatPercentChange(coin.price_change_percentage_24h)}
          </span>
        </div>
        <div className="mtf-rank">#{coin.market_cap_rank}</div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function RsiMtfPage() {
  const [signals, setSignals]         = useState({ long: [], short: [] });
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [assetMode, setAssetMode]     = useState('crypto');

  const scan = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // ── Stocks mode: VPS-only ──────────────────────────────────────────────
      if (assetMode === 'stocks') {
        if (!isServerApiAvailable()) {
          setError('Stocks data requires the VPS server. Start the server and refresh.');
          setIsLoading(false);
          return;
        }
        console.log('[RSI-MTF] Stocks mode — using pre-computed stock indicators…');
        const { stocks: serverStocks, lastUpdated: serverUpdated } = await fetchStocksMarketData();

        const longSignals  = [];
        const shortSignals = [];

        for (const stock of serverStocks) {
          if (!stock.indicators) continue;
          const { weekly, daily, fourHour } = stock.indicators;
          if (!weekly || !daily || !fourHour) continue;

          const direction = evaluateSignal(weekly.rsi, daily.rsi, fourHour.rsi);

          if (direction === 'long') {
            longSignals.push({
              ...stock,
              direction,
              weeklyRsi:   weekly.rsi,
              dailyRsi:    daily.rsi,
              fourHourRsi: fourHour.rsi,
            });
          } else if (direction === 'short') {
            shortSignals.push({
              ...stock,
              direction,
              weeklyRsi:   weekly.rsi,
              dailyRsi:    daily.rsi,
              fourHourRsi: fourHour.rsi,
            });
          }
        }

        setSignals({ long: longSignals, short: shortSignals });
        setLastUpdated(new Date(serverUpdated).getTime());
        setIsLoading(false);
        console.log(`[RSI-MTF] Stocks: ${longSignals.length} long, ${shortSignals.length} short`);
        return;
      }

      // ── VPS fast-path: use pre-computed indicators ─────────────────────────
      if (isServerApiAvailable()) {
        try {
          console.log('[RSI-MTF] VPS mode — using pre-computed indicators…');
          const { coins: serverCoins, lastUpdated: serverUpdated } = await fetchServerMarketData();

          const longSignals  = [];
          const shortSignals = [];

          for (const coin of serverCoins) {
            if (!coin.indicators) continue;
            const { weekly, daily, fourHour } = coin.indicators;
            if (!weekly || !daily || !fourHour) continue;

            const direction = evaluateSignal(weekly.rsi, daily.rsi, fourHour.rsi);

            if (direction === 'long') {
              longSignals.push({
                ...coin,
                direction,
                weeklyRsi:   weekly.rsi,
                dailyRsi:    daily.rsi,
                fourHourRsi: fourHour.rsi,
              });
            } else if (direction === 'short') {
              shortSignals.push({
                ...coin,
                direction,
                weeklyRsi:   weekly.rsi,
                dailyRsi:    daily.rsi,
                fourHourRsi: fourHour.rsi,
              });
            }
          }

          setSignals({ long: longSignals, short: shortSignals });
          setLastUpdated(new Date(serverUpdated).getTime());
          setIsLoading(false);
          console.log(`[RSI-MTF] VPS: ${longSignals.length} long, ${shortSignals.length} short`);
          return;
        } catch (vpsErr) {
          console.warn('[RSI-MTF] VPS fetch failed, falling back to live scan:', vpsErr.message);
        }
      }

      // ── Client-side fallback: fetch klines + compute indicators ─────────────
      const rawCoins = await fetchTopCoins(80);
      const coins    = rawCoins.filter(c => !isStablecoin(c.id));
      const symbolIndex = buildSymbolIndex(coins);
      const metaById = Object.fromEntries(coins.map(c => [c.id, c]));

      const metaByBinance = {};
      symbolIndex.forEach(({ coinGeckoId, binanceSymbol }) => {
        const meta = coins.find(c => c.id === coinGeckoId);
        if (meta) metaByBinance[binanceSymbol] = meta;
      });

      const mkReqs = (interval, limit) =>
        symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval, limit }));

      const [weeklyRaw, dailyRaw, fourHourRaw] = await Promise.all([
        fetchBinanceKlinesBatch(mkReqs('1w', 100)),
        fetchBinanceKlinesBatch(mkReqs('1d', 100)),
        fetchBinanceKlinesBatch(mkReqs('4h', 200)),
      ]);

      const longSignals  = [];
      const shortSignals = [];

      symbolIndex.forEach(({ coinGeckoId, binanceSymbol }) => {
        const meta = metaByBinance[binanceSymbol];
        if (!meta) return;

        const parseCandles = raw => {
          if (!raw || raw.length === 0) return null;
          return parseBinanceKlines(raw);
        };

        const wCandles  = parseCandles(weeklyRaw[binanceSymbol]);
        const dCandles  = parseCandles(dailyRaw[binanceSymbol]);
        const h4Candles = parseCandles(fourHourRaw[binanceSymbol]);

        if (!wCandles || !dCandles || !h4Candles) return;

        const { closes: wCloses }  = extractPrices(wCandles);
        const { closes: dCloses }  = extractPrices(dCandles);
        const { closes: h4Closes } = extractPrices(h4Candles);

        if (wCloses.length < 20 || dCloses.length < 20 || h4Closes.length < 55) return;

        const wRsiArr  = calculateRSI(wCloses);
        const dRsiArr  = calculateRSI(dCloses);
        const h4RsiArr = calculateRSI(h4Closes);

        const lastVal  = arr => arr[arr.length - 1] ?? null;
        const prevVal  = arr => arr[arr.length - 2] ?? null;

        const weeklyRsi       = lastVal(wRsiArr);
        const dailyRsi        = lastVal(dRsiArr);
        const fourHourRsi     = lastVal(h4RsiArr);
        const prevFourHourRsi = prevVal(h4RsiArr);

        const direction = evaluateSignal(weeklyRsi, dailyRsi, fourHourRsi);

        const entry = {
          id:         coinGeckoId,
          name:       meta.name,
          symbol:     meta.symbol,
          image:      meta.image,
          current_price:               meta.current_price,
          price_change_percentage_24h: meta.price_change_percentage_24h,
          market_cap_rank:             meta.market_cap_rank,
          direction,
          weeklyRsi,
          dailyRsi,
          fourHourRsi,
        };

        if (direction === 'long')  longSignals.push(entry);
        if (direction === 'short') shortSignals.push(entry);
      });

      setSignals({ long: longSignals, short: shortSignals });
      setLastUpdated(Date.now());

    } catch (err) {
      console.error('[RSI-MTF] Scan failed:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [assetMode]);

  useEffect(() => { scan(); }, [scan]);

  // ─── Asset mode toggle ────────────────────────────────────────────────────────
  const handleModeChange = (newMode) => {
    if (newMode === assetMode) return;
    setAssetMode(newMode);
    // scan() will be triggered by the useEffect dependency on assetMode via useCallback
  };

  const displayed = activeFilter === 'long'
    ? signals.long
    : activeFilter === 'short'
      ? signals.short
      : [...signals.long, ...signals.short].sort((a, b) =>
          (a.market_cap_rank || 999) - (b.market_cap_rank || 999)
        );

  const fourHourLabel = assetMode === 'stocks' ? '1H RSI' : '4H RSI';

  // ─── Loading state ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mtf-page">
        <div className="asset-toggle">
          <button
            className={`asset-toggle-btn ${assetMode === 'crypto' ? 'active' : ''}`}
            onClick={() => handleModeChange('crypto')}
          >
            🪙 Crypto
          </button>
          <button
            className={`asset-toggle-btn ${assetMode === 'stocks' ? 'active' : ''}`}
            onClick={() => handleModeChange('stocks')}
          >
            📈 US Stocks
          </button>
        </div>
        <div className="mtf-page-header">
          <h1 className="mtf-page-title">📡 RSI Multi-Timeframe</h1>
          <p className="mtf-page-subtitle">Scanning for RSI-MTF setups…</p>
        </div>
        <div className="mtf-loading-center">
          <Loader size="large" text="Scanning all timeframes…" />
        </div>
      </div>
    );
  }

  return (
    <div className="mtf-page">
      {/* Asset mode toggle */}
      <div className="asset-toggle">
        <button
          className={`asset-toggle-btn ${assetMode === 'crypto' ? 'active' : ''}`}
          onClick={() => handleModeChange('crypto')}
        >
          🪙 Crypto
        </button>
        <button
          className={`asset-toggle-btn ${assetMode === 'stocks' ? 'active' : ''}`}
          onClick={() => handleModeChange('stocks')}
        >
          📈 US Stocks
        </button>
      </div>

      {/* Header */}
      <div className="mtf-page-header">
        <div className="mtf-header-main">
          <h1 className="mtf-page-title">📡 RSI Multi-Timeframe</h1>
          <p className="mtf-page-subtitle">
            {assetMode === 'stocks'
              ? `Weekly RSI > 50 · Daily RSI > 45 (long) / ≤ 55 (short) · 1H RSI above/below 50`
              : `Weekly RSI > 50 · Daily RSI > 45 (long) / ≤ 55 (short) · 4H RSI above/below 50`
            }
          </p>
        </div>
        <button className="mtf-refresh-btn" onClick={scan}>↻ Refresh</button>
      </div>

      {error && (
        <ErrorBanner message={error} type="error" onDismiss={() => setError(null)} onRetry={scan} />
      )}

      {/* Strategy legend */}
      <div className="mtf-legend-box">
        <div className="mtf-legend-col long">
          <div className="mtf-legend-title">▲ Long Signal</div>
          <ul className="mtf-legend-list">
            <li>Weekly RSI &gt; 50</li>
            <li>Daily RSI &gt; 45</li>
            <li>{fourHourLabel} &gt; 50</li>
          </ul>
        </div>
        <div className="mtf-legend-col short">
          <div className="mtf-legend-title">▼ Short Signal</div>
          <ul className="mtf-legend-list">
            <li>Weekly RSI &lt; 50</li>
            <li>Daily RSI ≤ 55</li>
            <li>{fourHourLabel} &lt; 50</li>
          </ul>
        </div>
      </div>

      {/* Summary bar */}
      <div className="mtf-summary-bar">
        <div className="mtf-summary-stat">
          <span className="mtf-summary-number">{signals.long.length + signals.short.length}</span>
          <span className="mtf-summary-label">Total Signals</span>
        </div>
        <div className="mtf-summary-stat long-stat">
          <span className="mtf-summary-number">{signals.long.length}</span>
          <span className="mtf-summary-label">▲ Long</span>
        </div>
        <div className="mtf-summary-stat short-stat">
          <span className="mtf-summary-number">{signals.short.length}</span>
          <span className="mtf-summary-label">▼ Short</span>
        </div>
        {lastUpdated && (
          <div className="mtf-summary-updated">
            Updated {new Date(lastUpdated).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="mtf-filter-tabs">
        {['all', 'long', 'short'].map(f => (
          <button
            key={f}
            className={`mtf-filter-tab ${activeFilter === f ? 'active' : ''} ${f !== 'all' ? f : ''}`}
            onClick={() => setActiveFilter(f)}
          >
            {f === 'all'   ? '⬛ All'       : ''}
            {f === 'long'  ? '▲ Long only'  : ''}
            {f === 'short' ? '▼ Short only' : ''}
          </button>
        ))}
      </div>

      {/* Results grid */}
      {displayed.length === 0 ? (
        <div className="mtf-empty">
          <div className="mtf-empty-icon">🔍</div>
          <div className="mtf-empty-text">No RSI-MTF signals right now.</div>
          <div className="mtf-empty-sub">
            The strategy requires all four conditions to align simultaneously — this is uncommon by design.
          </div>
        </div>
      ) : (
        <div className="mtf-grid">
          {displayed.map(coin => (
            <SignalCard key={`${coin.id}-${coin.direction}`} coin={coin} assetMode={assetMode} />
          ))}
        </div>
      )}
    </div>
  );
}

export default RsiMtfPage;
